import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  MenuItem,
  useTheme,
  Alert,
  Checkbox,
  FormControlLabel,
  Paper,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Divider,
  Card,
  CardContent,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Upload,
  Schedule,
  Save,
  PictureAsPdf,
  Image as ImageIcon,
  Delete,
  CloudUpload,
  Close,
  Photo,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CreateBusinessRequest,
  UpdateBusinessRequest,
  BUSINESS_CATEGORIES,
  BUSINESS_SERVICES,
  BusinessHours,
  DocumentType,
} from '@/constants/types';
import { uploadService } from '@/services/upload/uploadService';
import { businessService } from '@/services/business/businessService';
import toast from 'react-hot-toast';
import { getFileUrl, getFileId, getFileName } from '@/utils/businessHelpers';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const NUMBER_OF_EMPLOYEES_OPTIONS = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5000+',
];

const validationSchema = Yup.object({
  businessName: Yup.string()
    .required('Business name is required')
    .min(2, 'Business name must be at least 2 characters')
    .max(100, 'Business name must not exceed 100 characters'),
  businessUsername: Yup.string()
    .required('Business username is required')
    .matches(/^@[a-zA-Z0-9_]{3,30}$/, 'Username must start with @ and contain 3-30 alphanumeric characters or underscores'),
  businessCategory: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one business category is required')
    .max(3, 'You can select up to 3 business categories')
    .required('Business category is required'),
  businessServices: Yup.array()
    .of(Yup.string())
    .max(5, 'You can select up to 5 services'),
  description: Yup.string().max(1000, 'Description must not exceed 1000 characters'),
  email: Yup.string()
    .required('Email is required')
    .email('Please enter a valid email'),
  website: Yup.string().url('Please enter a valid website URL'),
  contactNumber: Yup.string()
    .required('Contact number is required')
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit contact number'),
  whatsappNumber: Yup.string()
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit WhatsApp number'),
  landlineNumber: Yup.string()
    .matches(/^[0-9-+() ]{6,20}$/, 'Please enter a valid landline number'),
  addressLine1: Yup.string()
    .required('Address line 1 is required')
    .max(100, 'Address line 1 must not exceed 100 characters'),
  addressLine2: Yup.string().max(100, 'Address line 2 must not exceed 100 characters'),
  city: Yup.string()
    .required('City is required')
    .max(50, 'City must not exceed 50 characters'),
  state: Yup.string()
    .required('State is required')
    .max(50, 'State must not exceed 50 characters'),
  pincode: Yup.string()
    .required('Pincode is required')
    .max(10, 'Pincode must not exceed 10 characters'),
  landmark: Yup.string().max(100, 'Landmark must not exceed 100 characters'),
  ownerName: Yup.string()
    .required('Owner name is required')
    .max(100, 'Owner name must not exceed 100 characters'),
  // New fields
  gstNumber: Yup.string()
    .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Please enter a valid GST number (e.g., 22AAAAA0000A1Z5)'),
  panNumber: Yup.string()
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Please enter a valid PAN number (e.g., ABCDE1234F)'),
  companyStartDate: Yup.date().nullable(),
  numberOfEmployees: Yup.string(),
  googleMyBusinessLink: Yup.string().url('Please enter a valid Google My Business URL'),
  headOfficeAddress: Yup.object({
    addressLine1: Yup.string().max(100, 'Address line 1 must not exceed 100 characters'),
    addressLine2: Yup.string().max(100, 'Address line 2 must not exceed 100 characters'),
    city: Yup.string().max(50, 'City must not exceed 50 characters'),
    state: Yup.string().max(50, 'State must not exceed 50 characters'),
    pincode: Yup.string().max(10, 'Pincode must not exceed 10 characters'),
    landmark: Yup.string().max(100, 'Landmark must not exceed 100 characters'),
  }),
  socialMedia: Yup.object({
    instagram: Yup.string().url('Please enter a valid Instagram URL'),
    facebook: Yup.string().url('Please enter a valid Facebook URL'),
    youtube: Yup.string().url('Please enter a valid YouTube URL'),
    twitter: Yup.string().url('Please enter a valid Twitter/X URL'),
  }),
});

export const AddEditBusinessPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [activeStep, setActiveStep] = useState(0);
  const [createdBusinessId, setCreatedBusinessId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isBusinessVerified, setIsBusinessVerified] = useState(false);

  // Step 1: Basic Info
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoId, setLogoId] = useState<string>('');

  // Banner
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string>('');
  const [bannerId, setBannerId] = useState<string>('');

  // Head Office Address
  const [sameAsBusinessAddress, setSameAsBusinessAddress] = useState(false);

  const [businessHours, setBusinessHours] = useState<BusinessHours[]>(
    DAYS_OF_WEEK.map((day) => ({ day, openTime: '09:00', closeTime: '18:00', isClosed: false }))
  );

  // Step 2: Documents
  const [ownerIdProofPreview, setOwnerIdProofPreview] = useState<string>('');
  const [ownerIdProofId, setOwnerIdProofId] = useState<string>('');
  const [ownerIdProofFileName, setOwnerIdProofFileName] = useState<string>('');
  const [documents, setDocuments] = useState<Array<{ documentType: DocumentType; file?: File; fileId?: string; fileName: string; previewUrl?: string }>>([]);
  const [uploading, setUploading] = useState(false);

  const ownerIdProofInputRef = React.useRef<HTMLInputElement>(null);

  const formik = useFormik({
    initialValues: {
      businessName: '',
      businessUsername: '',
      businessCategory: [] as string[],
      businessServices: [] as string[],
      description: '',
      email: '',
      website: '',
      contactNumber: '',
      whatsappNumber: '',
      landlineNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      landmark: '',
      ownerName: '',
      // New fields
      gstNumber: '',
      panNumber: '',
      companyStartDate: '',
      numberOfEmployees: '',
      googleMyBusinessLink: '',
      headOfficeAddress: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pincode: '',
        landmark: '',
      },
      socialMedia: {
        instagram: '',
        facebook: '',
        youtube: '',
        twitter: '',
      },
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSaving(true);

        // Upload logo if new file is selected and not already uploaded
        let finalLogoId = logoId;
        if (logoFile && !logoId) {
          const logoUploadResponse = await uploadService.uploadSingle(logoFile, { folder: 'business/logos' });
          finalLogoId = logoUploadResponse.file.id;
          setLogoId(finalLogoId);
        }

        // Upload banner if new file is selected and not already uploaded
        let finalBannerId = bannerId;
        if (bannerFile && !bannerId) {
          const bannerUploadResponse = await uploadService.uploadSingle(bannerFile, { folder: 'business/banners' });
          finalBannerId = bannerUploadResponse.file.id;
          setBannerId(finalBannerId);
        }

        const businessData: any = {
          ...values,
          businessHours: businessHours,
        };

        // Add logo ID if exists
        if (finalLogoId) {
          businessData.logoId = finalLogoId;
        } else if (isEditMode) {
          businessData.logoId = null;
        }

        // Add banner ID if exists
        if (finalBannerId) {
          businessData.bannerId = finalBannerId;
        } else if (isEditMode) {
          businessData.bannerId = null;
        }

        if (isEditMode && id) {
          await businessService.updateBusiness(id, businessData as UpdateBusinessRequest);
          toast.success('Business information updated successfully');
          setActiveStep(1); // Move to documents step
        } else {
          const response = await businessService.createBusiness(businessData as CreateBusinessRequest);
          const newBusinessId = response.data.business._id;
          console.log('Created business with ID:', newBusinessId);
          setCreatedBusinessId(newBusinessId);
          toast.success('Business created successfully! Now upload documents.');
          setActiveStep(1); // Move to documents step
        }
      } catch (err: any) {
        toast.error(err.message || 'Failed to save business');
      } finally {
        setIsSaving(false);
      }
    },
  });

  useEffect(() => {
    if (isEditMode && id) {
      loadBusiness(id);
    }
  }, [id, isEditMode]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
      if (ownerIdProofPreview) {
        URL.revokeObjectURL(ownerIdProofPreview);
      }
      documents.forEach(doc => {
        if (doc.previewUrl) {
          URL.revokeObjectURL(doc.previewUrl);
        }
      });
    };
  }, []);

  const loadBusiness = async (businessId: string) => {
    try {
      setIsLoading(true);
      const response = await businessService.getBusiness(businessId);
      const businessData = response.data.business;
      setCreatedBusinessId(businessId);

      formik.setValues({
        businessName: businessData.businessName,
        businessUsername: businessData.businessUsername,
        businessCategory: Array.isArray(businessData.businessCategory)
          ? businessData.businessCategory
          : businessData.businessCategory ? [businessData.businessCategory] : [],
        businessServices: Array.isArray(businessData.businessServices)
          ? businessData.businessServices
          : businessData.businessServices ? [businessData.businessServices] : [],
        description: businessData.description || '',
        email: businessData.email,
        website: businessData.website || '',
        contactNumber: businessData.contactNumber,
        whatsappNumber: businessData.whatsappNumber || '',
        landlineNumber: businessData.landlineNumber || '',
        addressLine1: businessData.addressLine1,
        addressLine2: businessData.addressLine2 || '',
        city: businessData.city,
        state: businessData.state,
        pincode: businessData.pincode,
        landmark: businessData.landmark || '',
        ownerName: businessData.ownerName,
        // New fields
        gstNumber: businessData.gstNumber || '',
        panNumber: businessData.panNumber || '',
        companyStartDate: businessData.companyStartDate ? businessData.companyStartDate.split('T')[0] : '',
        numberOfEmployees: businessData.numberOfEmployees || '',
        googleMyBusinessLink: businessData.googleMyBusinessLink || '',
        headOfficeAddress: {
          addressLine1: businessData.headOfficeAddress?.addressLine1 || '',
          addressLine2: businessData.headOfficeAddress?.addressLine2 || '',
          city: businessData.headOfficeAddress?.city || '',
          state: businessData.headOfficeAddress?.state || '',
          pincode: businessData.headOfficeAddress?.pincode || '',
          landmark: businessData.headOfficeAddress?.landmark || '',
        },
        socialMedia: {
          instagram: businessData.socialMedia?.instagram || '',
          facebook: businessData.socialMedia?.facebook || '',
          youtube: businessData.socialMedia?.youtube || '',
          twitter: businessData.socialMedia?.twitter || '',
        },
      });

      const businessLogoId = getFileId(businessData.logoId);
      const businessBannerId = getFileId(businessData.bannerId);
      const businessOwnerIdProofId = getFileId(businessData.ownerIdProofId);

      setLogoId(businessLogoId);
      setLogoPreview(getFileUrl(businessData.logoId));
      setBannerId(businessBannerId);
      setBannerPreview(getFileUrl(businessData.bannerId));
      setOwnerIdProofId(businessOwnerIdProofId);
      setOwnerIdProofPreview(getFileUrl(businessData.ownerIdProofId));
      setOwnerIdProofFileName(getFileName(businessData.ownerIdProofId) || '');

      const mappedDocs = (businessData.documents || []).map(doc => ({
        documentType: doc.documentType,
        fileId: getFileId(doc.fileId),
        fileName: getFileName(doc.fileId) || 'Document',
        previewUrl: getFileUrl(doc.fileId), // Add preview URL for existing documents
      }));
      setDocuments(mappedDocs);

      if (businessData.businessHours && businessData.businessHours.length > 0) {
        setBusinessHours(businessData.businessHours);
      }

      // Set verified status
      setIsBusinessVerified(businessData.isVerified || false);

      // If editing and business exists, allow going to step 2
      setActiveStep(0);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load business');
      navigate('/my-business');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file size must be less than 2MB');
      return;
    }

    // Check if business exists (edit mode or after creation)
    const businessIdToUpdate = isEditMode ? id! : createdBusinessId;

    if (businessIdToUpdate) {
      // Auto-save mode: business exists
      try {
        setIsSaving(true);

        // Step 1: Delete old logo if re-uploading
        if (logoId) {
          try {
            await uploadService.deleteSingleFile(logoId);
            console.log('Old logo deleted:', logoId);
          } catch (error) {
            console.error('Failed to delete old logo:', error);
            // Continue even if delete fails
          }
        }

        // Step 2: Upload new logo
        const uploadResponse = await uploadService.uploadSingle(file, { folder: 'business/logos' });
        const newLogoId = uploadResponse.file.id;

        // Step 3: Update business record
        await businessService.updateBusiness(businessIdToUpdate, {
          logoId: newLogoId,
        });

        // Update state
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
        setLogoId(newLogoId);

        toast.success('Logo uploaded successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to upload logo');
      } finally {
        setIsSaving(false);
      }
    } else {
      // Manual save mode: business doesn't exist yet, just preview
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleBannerChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Banner file size must be less than 5MB');
      return;
    }

    const businessIdToUpdate = isEditMode ? id! : createdBusinessId;

    if (businessIdToUpdate) {
      try {
        setIsSaving(true);

        if (bannerId) {
          try {
            await uploadService.deleteSingleFile(bannerId);
          } catch (error) {
            console.error('Failed to delete old banner:', error);
          }
        }

        const uploadResponse = await uploadService.uploadSingle(file, { folder: 'business/banners' });
        const newBannerId = uploadResponse.file.id;

        await businessService.updateBusiness(businessIdToUpdate, {
          bannerId: newBannerId,
        });

        setBannerFile(file);
        setBannerPreview(URL.createObjectURL(file));
        setBannerId(newBannerId);

        toast.success('Banner uploaded successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to upload banner');
      } finally {
        setIsSaving(false);
      }
    } else {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleOwnerIdProofChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Document file size must be less than 2MB');
      return;
    }

    try {
      setUploading(true);

      // Step 1: Delete old file if re-uploading
      if (ownerIdProofId) {
        try {
          await uploadService.deleteSingleFile(ownerIdProofId);
          console.log('Old owner ID proof deleted:', ownerIdProofId);
        } catch (error) {
          console.error('Failed to delete old owner ID proof:', error);
          // Continue even if delete fails
        }
      }

      // Step 2: Upload new file
      const uploadResponse = await uploadService.uploadSingle(file, { folder: 'business/documents' });
      const newFileId = uploadResponse.file.id;

      // Step 3: Update business record
      const businessIdToUpdate = isEditMode ? id! : createdBusinessId;
      if (!businessIdToUpdate) {
        toast.error('Business ID not found');
        setUploading(false);
        return;
      }

      await businessService.updateBusiness(businessIdToUpdate, {
        ownerIdProofId: newFileId,
      });

      // Update state
      setOwnerIdProofPreview(URL.createObjectURL(file));
      setOwnerIdProofId(newFileId);
      setOwnerIdProofFileName(file.name);

      toast.success('Owner ID proof uploaded successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload owner ID proof');
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>, documentType: DocumentType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Document file size must be less than 2MB');
      return;
    }

    try {
      setUploading(true);

      // Step 1: Check if document of same type already exists (re-upload scenario)
      const existingDoc = documents.find(doc => doc.documentType === documentType);
      if (existingDoc && existingDoc.fileId) {
        try {
          await uploadService.deleteSingleFile(existingDoc.fileId);
          console.log(`Old ${documentType} document deleted:`, existingDoc.fileId);
        } catch (error) {
          console.error(`Failed to delete old ${documentType} document:`, error);
          // Continue even if delete fails
        }
      }

      // Step 2: Upload new file
      const uploadResponse = await uploadService.uploadSingle(file, { folder: 'business/documents' });
      const newFileId = uploadResponse.file.id;

      // Step 3: Build updated documents array (replace if exists, add if new)
      let updatedDocuments;
      if (existingDoc) {
        // Replace existing document
        updatedDocuments = documents
          .filter(doc => doc.documentType !== documentType)
          .map(doc => ({
            documentType: doc.documentType,
            fileId: doc.fileId || '',
          }))
          .concat([{
            documentType: documentType,
            fileId: newFileId,
          }]);
      } else {
        // Add new document
        updatedDocuments = [
          ...documents.map(doc => ({
            documentType: doc.documentType,
            fileId: doc.fileId || '',
          })),
          {
            documentType: documentType,
            fileId: newFileId,
          },
        ];
      }

      // Step 4: Update business record
      const businessIdToUpdate = isEditMode ? id! : createdBusinessId;
      if (!businessIdToUpdate) {
        toast.error('Business ID not found');
        setUploading(false);
        return;
      }

      await businessService.updateBusiness(businessIdToUpdate, {
        documents: updatedDocuments,
      });

      // Update state with preview URL
      const previewUrl = URL.createObjectURL(file);

      // Clean up old preview URL if re-uploading
      if (existingDoc && existingDoc.previewUrl) {
        URL.revokeObjectURL(existingDoc.previewUrl);
      }

      if (existingDoc) {
        // Replace in state
        setDocuments([
          ...documents.filter(doc => doc.documentType !== documentType),
          {
            documentType: documentType,
            fileId: newFileId,
            fileName: file.name,
            file: file,
            previewUrl: previewUrl,
          },
        ]);
      } else {
        // Add to state
        setDocuments([
          ...documents,
          {
            documentType: documentType,
            fileId: newFileId,
            fileName: file.name,
            file: file,
            previewUrl: previewUrl,
          },
        ]);
      }

      const action = existingDoc ? 're-uploaded' : 'uploaded';
      toast.success(`${documentType} document ${action} successfully`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDocument = async (index: number) => {
    const docToRemove = documents[index];

    try {
      setUploading(true);

      // Delete file from storage if it exists
      if (docToRemove.fileId) {
        await uploadService.deleteSingleFile(docToRemove.fileId);
      }

      // Get updated documents array (without the removed document)
      const updatedDocuments = documents
        .filter((_, i) => i !== index)
        .map(doc => ({
          documentType: doc.documentType,
          fileId: doc.fileId || '',
        }));

      // Update business record
      const businessIdToUpdate = isEditMode ? id! : createdBusinessId;
      if (businessIdToUpdate) {
        await businessService.updateBusiness(businessIdToUpdate, {
          documents: updatedDocuments,
        });
      }

      // Clean up preview URL
      if (docToRemove.previewUrl) {
        URL.revokeObjectURL(docToRemove.previewUrl);
      }

      // Update state
      setDocuments(documents.filter((_, i) => i !== index));

      toast.success(`${docToRemove.documentType} document deleted successfully`);
    } catch (error: any) {
      console.error('Failed to delete document:', error);
      toast.error(error.message || `Failed to delete ${docToRemove.documentType} document`);
    } finally {
      setUploading(false);
    }
  };

  const handleBusinessHoursChange = (index: number, field: keyof BusinessHours, value: string | boolean) => {
    const updatedHours = [...businessHours];
    updatedHours[index] = { ...updatedHours[index], [field]: value };
    setBusinessHours(updatedHours);
  };

  const steps = ['Business Information', 'Documents'];

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/my-business')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          {isEditMode ? 'Edit Business' : 'Add New Business'}
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step 1: Business Information */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3 }}>
      
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Alert severity="info">
                All businesses are submitted for approval and will be reviewed by the admin.
              </Alert>

              {/* Facebook-style Banner & Logo Upload */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ position: 'relative', mb: 8 }}>
                  {/* Banner Section */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: { xs: 150, sm: 200, md: 250 },
                      borderRadius: 3,
                      overflow: 'hidden',
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                    }}
                  >
                    {bannerPreview ? (
                      <>
                        <Box
                          component="img"
                          src={bannerPreview}
                          alt="Banner preview"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              setIsSaving(true);
                              if (bannerId) {
                                await uploadService.deleteSingleFile(bannerId);
                              }
                              const businessIdToUpdate = isEditMode ? id! : createdBusinessId;
                              if (businessIdToUpdate) {
                                await businessService.updateBusiness(businessIdToUpdate, { bannerId: '' });
                              }
                              if (bannerPreview) URL.revokeObjectURL(bannerPreview);
                              setBannerFile(null);
                              setBannerPreview('');
                              setBannerId('');
                              toast.success('Banner deleted successfully');
                            } catch (error: any) {
                              toast.error(error.message || 'Failed to delete banner');
                            } finally {
                              setIsSaving(false);
                            }
                          }}
                          disabled={isSaving}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        component="label"
                        sx={{
                          position: 'absolute',
                          bottom: 12,
                          right: 12,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          textTransform: 'none',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                        }}
                        startIcon={<Photo />}
                        size="small"
                        disabled={isSaving}
                      >
                        Add Cover Photo
                        <input type="file" hidden accept="image/*" onChange={handleBannerChange} />
                      </Button>
                    )}
                    {bannerPreview && (
                      <Button
                        component="label"
                        sx={{
                          position: 'absolute',
                          bottom: 12,
                          right: 12,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          textTransform: 'none',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                        }}
                        startIcon={<Photo />}
                        size="small"
                        disabled={isSaving}
                      >
                        Edit Cover
                        <input type="file" hidden accept="image/*" onChange={handleBannerChange} />
                      </Button>
                    )}
                  </Box>

                  {/* Logo Section - Overlapping */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -60,
                      left: { xs: 16, sm: 24 },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: { xs: 100, sm: 120, md: 140 },
                        height: { xs: 100, sm: 120, md: 140 },
                        borderRadius: '50%',
                        border: `4px solid ${theme.palette.background.paper}`,
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300',
                        overflow: 'hidden',
                        boxShadow: 2,
                      }}
                    >
                      {logoPreview ? (
                        <>
                          <Box
                            component="img"
                            src={logoPreview}
                            alt="Logo preview"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                setIsSaving(true);
                                if (logoId) {
                                  await uploadService.deleteSingleFile(logoId);
                                }
                                const businessIdToUpdate = isEditMode ? id! : createdBusinessId;
                                if (businessIdToUpdate) {
                                  await businessService.updateBusiness(businessIdToUpdate, { logoId: '' });
                                }
                                if (logoPreview) URL.revokeObjectURL(logoPreview);
                                setLogoFile(null);
                                setLogoPreview('');
                                setLogoId('');
                                toast.success('Logo deleted successfully');
                              } catch (error: any) {
                                toast.error(error.message || 'Failed to delete logo');
                              } finally {
                                setIsSaving(false);
                              }
                            }}
                            disabled={isSaving}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: '#fff',
                              width: 24,
                              height: 24,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                            }}
                          >
                            <Close sx={{ fontSize: 14 }} />
                          </IconButton>
                        </>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Photo sx={{ fontSize: 40, color: 'text.disabled' }} />
                        </Box>
                      )}
                      {!logoPreview && (
                        <Button
                          component="label"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            borderRadius: 0,
                            textTransform: 'none',
                            py: 0.5,
                            fontSize: '0.7rem',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                          size="small"
                          disabled={isSaving}
                        >
                          Add Logo
                          <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                        </Button>
                      )}
                      {logoPreview && (
                        <Button
                          component="label"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            borderRadius: 0,
                            textTransform: 'none',
                            py: 0.5,
                            fontSize: '0.7rem',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                          size="small"
                          disabled={isSaving}
                        >
                          Edit
                          <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Cover: 1600×600 px (8:3 ratio). Logo: Square 200×200 px. Max 5 MB each.
                </Typography>
              </Grid>
              {/* Basic Information */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="businessName"
                    label="Business Name"
                    value={formik.values.businessName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.businessName && Boolean(formik.errors.businessName)}
                    helperText={formik.touched.businessName && formik.errors.businessName}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="businessUsername"
                    label="Business Username"
                    placeholder="@mybusiness"
                    value={formik.values.businessUsername}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.businessUsername && Boolean(formik.errors.businessUsername)}
                    helperText={formik.touched.businessUsername && formik.errors.businessUsername}
                    required
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
               
                <Grid size={{ xs: 12, sm: 12 }}>
                  <TextField
                    fullWidth
                    name="ownerName"
                    label="Owner Name"
                    value={formik.values.ownerName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.ownerName && Boolean(formik.errors.ownerName)}
                    helperText={formik.touched.ownerName && formik.errors.ownerName}
                    required
                  />
                </Grid>
              </Grid>
 <Grid size={{ xs: 12, sm: 12 }}>
                  <Autocomplete
                    multiple
                    freeSolo
                        limitTags={2}
                    options={BUSINESS_CATEGORIES as readonly string[]}
                    value={formik.values.businessCategory}
                    onChange={(_, newValue) => {
                      const cleaned = (newValue as string[])
                        .map((v) => v.trim())
                        .filter((v) => v.length > 0);
                      const unique = Array.from(new Set(cleaned)).slice(0, 3);
                      formik.setFieldValue('businessCategory', unique);
                    }}
                    onBlur={() => formik.setFieldTouched('businessCategory', true)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Business Categories"
                        placeholder="Search or type your own…"
                        error={formik.touched.businessCategory && Boolean(formik.errors.businessCategory)}
                        helperText={
                          (formik.touched.businessCategory && formik.errors.businessCategory) ||
                          `Pick up to 3 (${formik.values.businessCategory.length}/3 selected)`
                        }
                        required
                      />
                    )}
                  />
                </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    multiple
                        limitTags={2}
                    freeSolo
                    options={BUSINESS_SERVICES as readonly string[]}
                    value={formik.values.businessServices}
                    onChange={(_, newValue) => {
                      const cleaned = (newValue as string[])
                        .map((v) => v.trim())
                        .filter((v) => v.length > 0);
                      const unique = Array.from(new Set(cleaned)).slice(0, 5);
                      formik.setFieldValue('businessServices', unique);
                    }}
                    onBlur={() => formik.setFieldTouched('businessServices', true)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Services Offered"
                        placeholder="Search or type your own…"
                        error={formik.touched.businessServices && Boolean(formik.errors.businessServices)}
                        helperText={
                          (formik.touched.businessServices && (formik.errors.businessServices as string)) ||
                          `Pick up to 5 (${formik.values.businessServices.length}/5 selected)`
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                name="description"
                label="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={
                  formik.touched.description && formik.errors.description
                    ? formik.errors.description
                    : `${formik.values.description.length}/1000 characters`
                }
                multiline
                rows={3}
                slotProps={{ htmlInput: { maxLength: 1000 } }}
              />

           

              {/* Company Details */}
              <Typography variant="subtitle1" fontWeight="bold">
                Company Details
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="gstNumber"
                    label="GST Number (Optional)"
                    placeholder="22AAAAA0000A1Z5"
                    value={formik.values.gstNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.gstNumber && Boolean(formik.errors.gstNumber)}
                    helperText={formik.touched.gstNumber && formik.errors.gstNumber}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="panNumber"
                    label="PAN Number (Optional)"
                    placeholder="ABCDE1234F"
                    value={formik.values.panNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.panNumber && Boolean(formik.errors.panNumber)}
                    helperText={formik.touched.panNumber && formik.errors.panNumber}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="companyStartDate"
                    label="Company Start Date (Optional)"
                    type="date"
                    value={formik.values.companyStartDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.companyStartDate && Boolean(formik.errors.companyStartDate)}
                    helperText={formik.touched.companyStartDate && formik.errors.companyStartDate}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    name="numberOfEmployees"
                    label="Number of Employees (Optional)"
                    value={formik.values.numberOfEmployees}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.numberOfEmployees && Boolean(formik.errors.numberOfEmployees)}
                    helperText={formik.touched.numberOfEmployees && formik.errors.numberOfEmployees}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {NUMBER_OF_EMPLOYEES_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    name="googleMyBusinessLink"
                    label="Google My Business Link (Optional)"
                    placeholder="https://g.page/yourbusiness"
                    value={formik.values.googleMyBusinessLink}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.googleMyBusinessLink && Boolean(formik.errors.googleMyBusinessLink)}
                    helperText={formik.touched.googleMyBusinessLink && formik.errors.googleMyBusinessLink}
                  />
                </Grid>
              </Grid>

              {/* Contact Information */}
              <Typography variant="subtitle1" fontWeight="bold">
                Contact Information
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Business Email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="website"
                    label="Website (Optional)"
                    placeholder="https://example.com"
                    value={formik.values.website}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.website && Boolean(formik.errors.website)}
                    helperText={formik.touched.website && formik.errors.website}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="contactNumber"
                    label="Contact Number"
                    placeholder="9876543210"
                    value={formik.values.contactNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.contactNumber && Boolean(formik.errors.contactNumber)}
                    helperText={formik.touched.contactNumber && formik.errors.contactNumber}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="whatsappNumber"
                    label="WhatsApp Number (Optional)"
                    placeholder="9876543210"
                    value={formik.values.whatsappNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.whatsappNumber && Boolean(formik.errors.whatsappNumber)}
                    helperText={formik.touched.whatsappNumber && formik.errors.whatsappNumber}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="landlineNumber"
                    label="Landline Number (Optional)"
                    placeholder="04423456789"
                    value={formik.values.landlineNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.landlineNumber && Boolean(formik.errors.landlineNumber)}
                    helperText={formik.touched.landlineNumber && formik.errors.landlineNumber}
                  />
                </Grid>
              </Grid>

              {/* Address Information */}
              <Typography variant="subtitle1" fontWeight="bold">
                Business Address
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    name="addressLine1"
                    label="Address Line 1"
                    value={formik.values.addressLine1}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
                    helperText={formik.touched.addressLine1 && formik.errors.addressLine1}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    name="addressLine2"
                    label="Address Line 2 (Optional)"
                    value={formik.values.addressLine2}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.addressLine2 && Boolean(formik.errors.addressLine2)}
                    helperText={formik.touched.addressLine2 && formik.errors.addressLine2}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    name="city"
                    label="City"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    name="state"
                    label="State"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.state && Boolean(formik.errors.state)}
                    helperText={formik.touched.state && formik.errors.state}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    name="pincode"
                    label="Pincode"
                    value={formik.values.pincode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                    helperText={formik.touched.pincode && formik.errors.pincode}
                    required
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                name="landmark"
                label="Landmark (Optional)"
                value={formik.values.landmark}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.landmark && Boolean(formik.errors.landmark)}
                helperText={formik.touched.landmark && formik.errors.landmark}
              />
    {/* Head Office Address */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Head Office Address (Optional)
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sameAsBusinessAddress}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSameAsBusinessAddress(checked);
                        if (checked) {
                          formik.setFieldValue('headOfficeAddress', {
                            addressLine1: formik.values.addressLine1,
                            addressLine2: formik.values.addressLine2,
                            city: formik.values.city,
                            state: formik.values.state,
                            pincode: formik.values.pincode,
                            landmark: formik.values.landmark,
                          });
                        }
                      }}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Same as Business Address</Typography>}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    name="headOfficeAddress.addressLine1"
                    label="Address Line 1"
                    value={sameAsBusinessAddress ? formik.values.addressLine1 : formik.values.headOfficeAddress.addressLine1}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.addressLine1 && Boolean(formik.errors.headOfficeAddress?.addressLine1)}
                    helperText={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.addressLine1 && formik.errors.headOfficeAddress?.addressLine1}
                    disabled={sameAsBusinessAddress}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    name="headOfficeAddress.addressLine2"
                    label="Address Line 2"
                    value={sameAsBusinessAddress ? formik.values.addressLine2 : formik.values.headOfficeAddress.addressLine2}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.addressLine2 && Boolean(formik.errors.headOfficeAddress?.addressLine2)}
                    helperText={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.addressLine2 && formik.errors.headOfficeAddress?.addressLine2}
                    disabled={sameAsBusinessAddress}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    name="headOfficeAddress.city"
                    label="City"
                    value={sameAsBusinessAddress ? formik.values.city : formik.values.headOfficeAddress.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.city && Boolean(formik.errors.headOfficeAddress?.city)}
                    helperText={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.city && formik.errors.headOfficeAddress?.city}
                    disabled={sameAsBusinessAddress}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    name="headOfficeAddress.state"
                    label="State"
                    value={sameAsBusinessAddress ? formik.values.state : formik.values.headOfficeAddress.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.state && Boolean(formik.errors.headOfficeAddress?.state)}
                    helperText={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.state && formik.errors.headOfficeAddress?.state}
                    disabled={sameAsBusinessAddress}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    name="headOfficeAddress.pincode"
                    label="Pincode"
                    value={sameAsBusinessAddress ? formik.values.pincode : formik.values.headOfficeAddress.pincode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.pincode && Boolean(formik.errors.headOfficeAddress?.pincode)}
                    helperText={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.pincode && formik.errors.headOfficeAddress?.pincode}
                    disabled={sameAsBusinessAddress}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    name="headOfficeAddress.landmark"
                    label="Landmark"
                    value={sameAsBusinessAddress ? formik.values.landmark : formik.values.headOfficeAddress.landmark}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.landmark && Boolean(formik.errors.headOfficeAddress?.landmark)}
                    helperText={!sameAsBusinessAddress && formik.touched.headOfficeAddress?.landmark && formik.errors.headOfficeAddress?.landmark}
                    disabled={sameAsBusinessAddress}
                  />
                </Grid>
              </Grid>
              {/* Business Hours */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule /> Business Hours (Optional)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {businessHours.map((hours, index) => (
                    <Box
                      key={hours.day}
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {hours.day}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <TextField
                            fullWidth
                            label="Open Time"
                            type="time"
                            size="small"
                            value={hours.openTime || ''}
                            onChange={(e) => handleBusinessHoursChange(index, 'openTime', e.target.value)}
                            disabled={hours.isClosed}
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <TextField
                            fullWidth
                            label="Close Time"
                            type="time"
                            size="small"
                            value={hours.closeTime || ''}
                            onChange={(e) => handleBusinessHoursChange(index, 'closeTime', e.target.value)}
                            disabled={hours.isClosed}
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={hours.isClosed}
                                onChange={(e) => handleBusinessHoursChange(index, 'isClosed', e.target.checked)}
                              />
                            }
                            label="Closed"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              </Box>

          

              {/* Social Media */}
              <Typography variant="subtitle1" fontWeight="bold">
                Social Media Links (Optional)
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="socialMedia.instagram"
                    label="Instagram URL"
                    placeholder="https://instagram.com/..."
                    value={formik.values.socialMedia.instagram}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.socialMedia?.instagram && Boolean(formik.errors.socialMedia?.instagram)}
                    helperText={formik.touched.socialMedia?.instagram && formik.errors.socialMedia?.instagram}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="socialMedia.facebook"
                    label="Facebook URL"
                    placeholder="https://facebook.com/..."
                    value={formik.values.socialMedia.facebook}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.socialMedia?.facebook && Boolean(formik.errors.socialMedia?.facebook)}
                    helperText={formik.touched.socialMedia?.facebook && formik.errors.socialMedia?.facebook}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="socialMedia.youtube"
                    label="YouTube URL"
                    placeholder="https://youtube.com/..."
                    value={formik.values.socialMedia.youtube}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.socialMedia?.youtube && Boolean(formik.errors.socialMedia?.youtube)}
                    helperText={formik.touched.socialMedia?.youtube && formik.errors.socialMedia?.youtube}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="socialMedia.twitter"
                    label="Twitter/X URL"
                    placeholder="https://twitter.com/..."
                    value={formik.values.socialMedia.twitter}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.socialMedia?.twitter && Boolean(formik.errors.socialMedia?.twitter)}
                    helperText={formik.touched.socialMedia?.twitter && formik.errors.socialMedia?.twitter}
                  />
                </Grid>
              </Grid>

              {/* Actions */}
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={() => navigate('/my-business')} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  // type="submit"
                  onClick={(e:any)=>formik.handleSubmit(e)}
                  variant="contained"
                  disabled={isSaving}
                  startIcon={<Save />}
                >
                  {isSaving ? 'Saving...' : 'Save & Continue'}
                </Button>
              </Box>
            </Box>
        
        </Paper>
      )}

      {/* Step 2: Documents */}
      {activeStep === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="success">
              Business information saved and submitted for verification!
            </Alert>

            {/* Owner ID Proof */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Owner ID Proof (Optional but required for verification, Max 2MB)
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: ownerIdProofPreview ? 'success.main' : 'divider',
                      borderRadius: 2,
                      height: '100%',
                      minHeight: 200,
                    }}
                  >
                    {ownerIdProofPreview ? (
                      // Preview mode
                      <>
                        <Box
                          sx={{
                            height: 140,
                            bgcolor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => {
                            if (ownerIdProofPreview) {
                              window.open(ownerIdProofPreview, '_blank');
                            }
                          }}
                          title="Click to preview"
                        >
                          <PictureAsPdf sx={{ fontSize: 64, color: 'error.main' }} />
                          {!isBusinessVerified && (
                            <IconButton
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation(); // Prevent triggering preview
                                try {
                                  setUploading(true);

                                  // Delete file from storage if it exists
                                  if (ownerIdProofId) {
                                    console.log('Deleting owner ID proof from storage:', ownerIdProofId);
                                    await uploadService.deleteSingleFile(ownerIdProofId);
                                    console.log('Owner ID proof deleted from storage');
                                  }

                                  // Update business record
                                  const businessIdToUpdate = isEditMode ? id! : createdBusinessId;
                                  if (businessIdToUpdate) {
                                    console.log('Updating business to remove owner ID proof reference');
                                    await businessService.updateBusiness(businessIdToUpdate, {
                                      ownerIdProofId: '',
                                    });
                                    console.log('Business updated successfully');
                                  }

                                  // Reset file input
                                  if (ownerIdProofInputRef.current) {
                                    ownerIdProofInputRef.current.value = '';
                                  }

                                  // Clean up preview URL
                                if (ownerIdProofPreview) {
                                  URL.revokeObjectURL(ownerIdProofPreview);
                                }

                                // Clear state
                                setOwnerIdProofPreview('');
                                setOwnerIdProofId('');
                                setOwnerIdProofFileName('');

                                toast.success('Owner ID proof deleted successfully');
                              } catch (error: any) {
                                console.error('Failed to delete owner ID proof:', error);
                                toast.error(error.message || 'Failed to delete owner ID proof');
                              } finally {
                                setUploading(false);
                              }
                            }}
                            disabled={uploading}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'background.paper',
                              '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                          )}
                        </Box>
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            Owner ID Proof
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {ownerIdProofFileName}
                          </Typography>
                          {!isBusinessVerified ? (
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              fullWidth
                              disabled={uploading}
                              startIcon={<CloudUpload />}
                              sx={{ mt: 1, textTransform: 'none' }}
                            >
                              Re-upload
                              <input
                                ref={ownerIdProofInputRef}
                                type="file"
                                hidden
                                accept="application/pdf"
                                onChange={handleOwnerIdProofChange}
                              />
                            </Button>
                          ) : (
                            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                              Cannot modify verified business
                            </Typography>
                          )}
                        </CardContent>
                      </>
                    ) : (
                      // Upload mode
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 2,
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          Owner ID Proof
                        </Typography>
                        {!isBusinessVerified ? (
                          <Button
                            variant="contained"
                            component="label"
                            startIcon={<Upload />}
                            size="small"
                            disabled={uploading}
                            sx={{
                              bgcolor: 'success.main',
                              '&:hover': { bgcolor: 'success.dark' },
                              textTransform: 'none',
                            }}
                          >
                            Upload
                            <input
                              ref={ownerIdProofInputRef}
                              type="file"
                              hidden
                              accept="application/pdf"
                              onChange={handleOwnerIdProofChange}
                            />
                          </Button>
                        ) : (
                          <Typography variant="caption" color="warning.main" sx={{ mt: 1, textAlign: 'center' }}>
                            Cannot modify verified business
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Additional Documents */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Business Documents (Optional but required for verification, Max 2MB each)
              </Typography>
              <Grid container spacing={2}>
                {(['GST', 'License', 'Registration'] as const).map((docType) => {
                  const existingDoc = documents.find(doc => doc.documentType === docType);

                  return (
                    <Grid size={{ xs: 12, sm: 4 }} key={docType}>
                      <Card
                        elevation={0}
                        sx={{
                          border: '1px solid',
                          borderColor: existingDoc ? 'success.main' : 'divider',
                          borderRadius: 2,
                          height: '100%',
                          minHeight: 200,
                        }}
                      >
                        {existingDoc && existingDoc.previewUrl ? (
                          // Preview mode
                          <>
                            <Box
                              sx={{
                                height: 140,
                                bgcolor: 'grey.100',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8,
                                },
                              }}
                              onClick={() => {
                                if (existingDoc.previewUrl) {
                                  window.open(existingDoc.previewUrl, '_blank');
                                }
                              }}
                              title="Click to preview"
                            >
                              <PictureAsPdf sx={{ fontSize: 64, color: 'error.main' }} />
                              {!isBusinessVerified && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering preview
                                    const docIndex = documents.findIndex(d => d.documentType === docType);
                                    handleRemoveDocument(docIndex);
                                  }}
                                  disabled={uploading}
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: 'background.paper',
                                    '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                              )}
                            </Box>
                            <CardContent sx={{ p: 1.5 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {docType}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {existingDoc.fileName}
                              </Typography>
                              {!isBusinessVerified ? (
                                <Button
                                  variant="outlined"
                                  component="label"
                                  size="small"
                                  fullWidth
                                  disabled={uploading}
                                  startIcon={<CloudUpload />}
                                  sx={{ mt: 1, textTransform: 'none' }}
                                >
                                  Re-upload
                                  <input
                                    type="file"
                                    hidden
                                    accept="application/pdf"
                                    onChange={(e) => handleDocumentUpload(e, docType)}
                                  />
                                </Button>
                              ) : (
                                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                  Cannot modify verified business
                                </Typography>
                              )}
                            </CardContent>
                          </>
                        ) : (
                          // Upload mode
                          <Box
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 2,
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                              {docType}
                            </Typography>
                            {!isBusinessVerified ? (
                              <Button
                                variant="contained"
                                component="label"
                                startIcon={<Upload />}
                                size="small"
                                disabled={uploading}
                                sx={{
                                  bgcolor: 'success.main',
                                  '&:hover': { bgcolor: 'success.dark' },
                                  textTransform: 'none',
                                }}
                              >
                                Upload
                                <input
                                  type="file"
                                  hidden
                                  accept="application/pdf"
                                onChange={(e) => handleDocumentUpload(e, docType)}
                              />
                            </Button>
                            ) : (
                              <Typography variant="caption" color="warning.main" sx={{ mt: 1, textAlign: 'center' }}>
                                Cannot modify verified business
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Actions */}
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Button
                onClick={() => setActiveStep(0)}
                disabled={uploading}
              >
                Back to Business Info
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/my-business')}
                disabled={uploading}
              >
                Done
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
